import { Zap, ShieldCheck, Star, Calendar } from "lucide-react";
import {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardContent,
  CardDecoration,
  CardBadge,
} from "@/components/modern-ui/card";

const features = [
  {
    icon: Zap,
    title: "Instant AI responses",
    desc: "Someone reaches out and gets a real answer in seconds, not a dead form or a read receipt.",
    badge: "Sub-3s replies",
    badgeClass:
      "border-violet-500/25 bg-violet-500/10 text-violet-200/90",
    decorationClass: "bg-violet-500/[0.14]",
    iconWrapClass: "border-violet-500/20 bg-violet-500/[0.08] text-violet-200",
  },
  {
    icon: ShieldCheck,
    title: "Smart qualification",
    desc: "Budget, scope, and fit get surfaced early so you only spend time on conversations that make sense.",
    badge: "Early signals",
    badgeClass:
      "border-fuchsia-500/25 bg-fuchsia-500/10 text-fuchsia-200/90",
    decorationClass: "bg-fuchsia-500/[0.12]",
    iconWrapClass: "border-fuchsia-500/20 bg-fuchsia-500/[0.08] text-fuchsia-200",
  },
  {
    icon: Star,
    title: "Priority messages",
    desc: "High-intent threads rise to the top. Everything else stays out of your way until you want it.",
    badge: "Intent ranking",
    badgeClass:
      "border-violet-400/25 bg-violet-400/[0.08] text-violet-100/85",
    decorationClass: "bg-violet-400/[0.1]",
    iconWrapClass: "border-violet-400/20 bg-violet-400/[0.07] text-violet-100/90",
  },
  {
    icon: Calendar,
    title: "Seamless booking",
    desc: "When a lead is qualified, they land on your calendar. No extra email tennis to find a slot.",
    badge: "Calendar handoff",
    badgeClass:
      "border-fuchsia-400/25 bg-fuchsia-400/[0.08] text-fuchsia-100/85",
    decorationClass: "bg-fuchsia-400/[0.1]",
    iconWrapClass: "border-fuchsia-400/20 bg-fuchsia-400/[0.07] text-fuchsia-100/90",
  },
];

const featureCardClassName =
  "group flex h-full min-h-[12.5rem] flex-col overflow-visible border-violet-500/12 bg-gradient-to-br from-[#1a1726] via-[#13111c] to-[#0e0c14] shadow-md shadow-black/20 ring-1 ring-white/[0.03] transition-transform duration-300 hover:-translate-y-1 hover:border-violet-500/20 hover:shadow-lg hover:shadow-violet-950/25";

export default function FeaturesSection() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-14 sm:py-[4.25rem]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="reveal text-center mb-10 sm:mb-11">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-violet-300/55 mb-3">
            Features
          </p>
          <h2
            data-testid="features-headline"
            className="text-3xl sm:text-4xl tracking-tight font-semibold max-w-2xl mx-auto leading-tight"
          >
            Everything you need.
            <br />
            <span className="text-violet-200/35">Nothing you don&apos;t.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 reveal stagger-children">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <Card
                key={f.title}
                data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                className={featureCardClassName}
              >
                <CardDecoration className={f.decorationClass} />
                <CardHeader className="relative z-[1] pb-3">
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border [&_svg]:h-[1.35rem] [&_svg]:w-[1.35rem] ${f.iconWrapClass}`}
                      aria-hidden
                    >
                      <Icon />
                    </div>
                    <CardTitle className="min-w-0 text-[1.0625rem] font-semibold tracking-tight text-zinc-100">
                      {f.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="relative z-[1] flex-1">
                  <p className="text-[0.875rem] leading-[1.65] text-zinc-400 pb-1">
                    {f.desc}
                  </p>
                </CardContent>
                <CardFooter className="relative z-[1] mt-auto">
                  <CardBadge className={f.badgeClass}>{f.badge}</CardBadge>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
